/**
 * Settings → Scheduling → Sales coverage — `docs/new-request.md` §14.1,
 * Board 11.
 *
 * Who is recommended for which requests: one grid per branch, a row per
 * person who sells there, an **R** and a **C** under every active division.
 * One click toggles a tick and saves it; the row it writes is the one New
 * request's recommendation reads (§9.2) and the person's Sells card shows
 * (§14.2). Under it, the four steps recommendation follows, the gaps — every
 * pair nobody covers in amber, every pair one person carries in slate — and
 * the company's **Site visits** card: the arrival window and the confirmation
 * default, the same in every branch.
 *
 * Everything the grid says is the server's answer (`/sales-coverage`); the
 * page draws it and sends one tick at a time.
 */
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ARRIVAL_WINDOWS, arrivalWords, can, type MeResponse, type Segment,
} from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { request } from '../lib/api.js'
import { usePhone } from '../lib/use-narrow.js'
import { useSettingsBar } from './settings-bar.js'
import { Avatar, buttonClass, EmptyState, Segmented, Switch } from '../components/primitives.js'
import { MenuSelect } from '../components/menu-select.js'
import { Popover } from '../components/overlays.js'
import { PanelSheet } from '../components/panel-sheet.js'
import { Tile } from '../components/form/icon-picker.js'
import { IconInfo, IconPlus, IconSearch, IconWarn } from '../frame/icons.js'

interface Tick { divisionId: string; segment: Segment }

interface Grid {
  branch: { id: string; name: string } | null
  branches: { id: string; name: string }[]
  divisions: { id: string; code: string; name: string; colour: string; icon: string }[]
  people: {
    id: string; name: string; initials: string; openRequests: number
    startsFrom: string; works: string; ticks: Tick[]
  }[]
  notCovered: Tick[]
  single: (Tick & { name: string })[]
}

interface SiteVisits { arrivalMinutes: number; confirmDefault: boolean }

const SEGMENT_LETTER: Record<Segment, 'R' | 'C'> = { residential: 'R', commercial: 'C' }
const SEGMENT_WORD: Record<Segment, string> = { residential: 'Residential', commercial: 'Commercial' }
const BOTH: Segment[] = ['residential', 'commercial']

const plural = (count: number, one: string, many: string) => `${count} ${count === 1 ? one : many}`

/** An example start for the arrival window's sentence: 10:00 AM. */
const EXAMPLE_START = 600

// ---------------------------------------------------------------------------
// The R and the C (Board 11: solid green on, dashed off, amber where nobody)
// ---------------------------------------------------------------------------

function Letter({ letter, look, onClick, label }: {
  letter: 'R' | 'C'
  look: 'on' | 'off' | 'gap' | 'none'
  onClick?: () => void
  label?: string
}) {
  const box = cn('inline-flex h-6 w-[26px] shrink-0 items-center justify-center rounded-[7px] text-[11.5px] font-bold',
    look === 'on' && 'bg-accent text-surface',
    look === 'off' && 'border border-dashed border-line-strong bg-surface text-text-faint',
    look === 'gap' && 'border border-dashed border-amber-dash bg-amber-band-bg text-amber-band-text',
    look === 'none' && 'invisible')
  if (!onClick) return <span aria-hidden={look === 'none' || undefined} className={box}>{letter}</span>
  return (
    <button type="button" role="switch" aria-checked={look === 'on'} aria-label={label} onClick={onClick}
      className={cn(box, look === 'off' && 'hover:border-accent-line hover:text-accent')}>
      {letter}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Add a salesperson: anyone active, into this branch's grid, nothing ticked
// ---------------------------------------------------------------------------

function AddPerson({ orgUnitId, anchor, open, onClose, onAdded }: {
  orgUnitId: string
  anchor: HTMLElement | null
  open: boolean
  onClose: () => void
  onAdded: (grid: Grid) => void
}) {
  const phone = usePhone()
  const [text, setText] = useState('')
  const [items, setItems] = useState<{ id: string; name: string; line: string }[]>([])
  const [problem, setProblem] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let live = true
    const timer = window.setTimeout(() => {
      request<{ items: { id: string; name: string; line: string }[] }>(
        `/sales-coverage/people/search?q=${encodeURIComponent(text.trim())}&orgUnitId=${orgUnitId}`)
        .then((answer) => { if (live) setItems(answer.items) })
        .catch(() => { if (live) setItems([]) })
    }, 150)
    return () => { live = false; window.clearTimeout(timer) }
  }, [open, text, orgUnitId])

  useEffect(() => { if (!open) { setText(''); setProblem(null) } }, [open])

  const add = async (userId: string) => {
    try {
      onAdded(await request<Grid>('/sales-coverage/people', { method: 'POST', body: { orgUnitId, userId } }))
      onClose()
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'They could not be added.')
    }
  }

  const body = (
    <div className="flex flex-col gap-1 p-2">
      <label className={cn('flex items-center gap-2 rounded-control border border-line bg-surface px-2.5',
        phone ? 'h-11' : 'h-9', 'focus-within:border-accent-line focus-within:ring-2 focus-within:ring-accent-ring')}>
        <span className="inline-flex text-text-faint"><IconSearch size={15} /></span>
        <input
          autoFocus
          value={text}
          aria-label="Find a person"
          placeholder="Find a person…"
          onChange={(event) => setText(event.target.value)}
          className="min-w-0 flex-1 bg-transparent text-[13.5px] text-text outline-none focus-visible:shadow-none placeholder:text-text-faint"
        />
      </label>
      <div className="max-h-72 overflow-y-auto">
        {items.length === 0 && (
          <p className="m-0 px-2 py-3 text-[12.5px] text-text-muted">Nobody else to add.</p>
        )}
        {items.map((person) => (
          <button key={person.id} type="button" onClick={() => void add(person.id)}
            className={cn('flex w-full items-center gap-2.5 rounded-button px-2 text-left hover:bg-hover',
              phone ? 'h-[52px]' : 'h-11')}>
            <Avatar name={person.name} size={28} />
            <span className="flex min-w-0 flex-col">
              <b className="truncate text-[13px] font-semibold text-text">{person.name}</b>
              {person.line && <span className="truncate text-[11.5px] text-text-muted">{person.line}</span>}
            </span>
          </button>
        ))}
      </div>
      {problem && <p className="m-0 px-2 text-[12px] text-danger">{problem}</p>}
    </div>
  )

  if (phone) {
    return (
      <PanelSheet open={open} onClose={onClose} title="Add a salesperson"
        sub="into this branch’s grid, nothing ticked">
        {body}
      </PanelSheet>
    )
  }
  return (
    <Popover open={open} anchor={anchor} onClose={onClose} align="right" width={320} label="Add a salesperson">
      {body}
    </Popover>
  )
}

// ---------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------

export function SalesCoveragePage({ me }: { me: MeResponse }) {
  const navigate = useNavigate()
  const phone = usePhone()
  const canEdit = can(me.permissions, 'company_settings', 'edit')
  const [grid, setGrid] = useState<Grid | null>(null)
  const [visits, setVisits] = useState<SiteVisits | null>(null)
  const [problem, setProblem] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const addButton = useRef<HTMLButtonElement>(null)

  const load = useCallback(async (orgUnitId: string | null) => {
    try {
      setGrid(await request<Grid>(`/sales-coverage${orgUnitId ? `?orgUnitId=${orgUnitId}` : ''}`))
      setProblem(null)
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : 'Sales coverage could not be loaded.')
    }
  }, [])

  useEffect(() => {
    void load(null)
    request<SiteVisits>('/sales-coverage/site-visits').then(setVisits).catch(() => setVisits(null))
  }, [load])

  const bar = useSettingsBar({
    title: 'Sales coverage',
    sub: 'who is recommended for which requests',
    actions: canEdit && grid?.branch
      ? (
        <button ref={addButton} type="button" onClick={() => setAdding(true)}
          className={buttonClass({ variant: 'primary', className: 'h-8 text-[12.5px]' })}>
          <IconPlus size={14} />Add a salesperson
        </button>
      )
      : undefined,
  })

  /* One click, one save; the grid comes back as the server now has it. */
  const toggle = async (personId: string, tick: Tick, on: boolean) => {
    if (!grid?.branch || !canEdit) return
    const before = grid
    setGrid({
      ...grid,
      people: grid.people.map((person) => (person.id !== personId ? person : {
        ...person,
        ticks: on ? [...person.ticks, tick]
          : person.ticks.filter((one) => one.divisionId !== tick.divisionId || one.segment !== tick.segment),
      })),
    })
    try {
      setGrid(await request<Grid>('/sales-coverage/ticks', {
        method: 'POST', body: { orgUnitId: grid.branch.id, userId: personId, ...tick, on },
      }))
    } catch (cause) {
      setGrid(before)
      setProblem(cause instanceof Error ? cause.message : 'That tick did not save.')
    }
  }

  const saveVisits = async (change: Partial<SiteVisits>) => {
    if (!visits || !canEdit) return
    const before = visits
    setVisits({ ...visits, ...change })
    try {
      setVisits(await request<SiteVisits>('/sales-coverage/site-visits', { method: 'PATCH', body: change }))
    } catch (cause) {
      setVisits(before)
      setProblem(cause instanceof Error ? cause.message : 'That did not save.')
    }
  }

  if (!grid) {
    return (
      <div className="p-5">
        {bar}
        {problem ? <EmptyState title="Not available" body={problem} />
          : <p className="m-0 text-body text-text-muted">Loading…</p>}
      </div>
    )
  }

  const divisionName = (id: string) => grid.divisions.find((one) => one.id === id)?.name ?? ''
  const has = (person: Grid['people'][number], tick: Tick) =>
    person.ticks.some((one) => one.divisionId === tick.divisionId && one.segment === tick.segment)
  const gapsIn = (divisionId: string) => grid.notCovered.filter((one) => one.divisionId === divisionId)
  /*
   * The board's 200 · 112 · … · 1fr is drawn beside a 74px rail; the real nav
   * is 224, so on a desktop the columns give a little — never below what a
   * division's name and its R and C need — and the last one wraps rather than
   * clipping. A phone keeps the board's widths and scrolls inside the card.
   */
  const columns = phone
    ? `200px repeat(${grid.divisions.length}, 112px) minmax(150px, 1fr)`
    : `184px repeat(${grid.divisions.length}, minmax(96px, 112px)) minmax(120px, 1fr)`
  const rowWidth = phone ? 'min-w-max' : ''
  /* Below this the grid scrolls inside its card as one piece (28 is the row's padding). */
  const rowStyle = phone ? { gridTemplateColumns: columns }
    : { gridTemplateColumns: columns, minWidth: 184 + 96 * grid.divisions.length + 120 + 28 }
  const branchName = grid.branch?.name ?? 'the branch'

  return (
    <div className={cn('flex flex-col', phone ? 'gap-3.5 p-4' : 'px-6 py-5')}>
      {bar}

      {/* The branch, how many, and the legend. */}
      <div className={cn('flex gap-3', phone ? 'flex-col items-stretch' : 'items-center')}>
        <div className={phone ? 'w-full' : 'w-[220px]'}>
          <MenuSelect
            label="Branch"
            size="field"
            value={grid.branch?.id ?? null}
            options={grid.branches.map((one) => ({ value: one.id, label: one.name }))}
            noSearch={grid.branches.length < 8}
            sheet={{ title: 'Branch' }}
            onChange={(id) => { if (id) void load(id) }}
          />
        </div>
        <span className="text-[12.5px] text-text-muted">
          {plural(grid.people.length, 'salesperson', 'salespeople')} · {plural(grid.divisions.length, 'division', 'divisions')}
        </span>
        <span className="flex-1" />
        <span className="inline-flex items-center gap-1.5 text-[12px] text-text-muted">
          <span className="flex items-center gap-1">
            <Letter letter="R" look="on" />
            <Letter letter="C" look="off" />
          </span>
          residential · commercial
        </span>
      </div>

      {problem && grid && (
        <p role="alert" className="m-0 mt-3 text-[12.5px] text-danger">{problem}</p>
      )}

      {/* The grid. On a phone it scrolls sideways inside its card, never the page. */}
      <div className={cn('overflow-hidden rounded-record border border-line bg-surface', !phone && 'mt-3.5')}>
        <div className="overflow-x-auto">
          <div style={rowStyle}
            className={cn('grid h-11 items-center border-b border-line bg-surface-alt px-3.5 text-[11.5px] font-semibold text-text-muted', rowWidth)}>
            <span>Salesperson</span>
            {grid.divisions.map((division) => (
              <span key={division.id} title={division.name} className="inline-flex min-w-0 items-center gap-1.5">
                <Tile set="division" slug={division.icon} colour={division.colour} size={16} />
                <span className="truncate">{division.name}</span>
              </span>
            ))}
            <span>Starts from · works</span>
          </div>

          {grid.people.map((person) => (
            <div key={person.id} style={rowStyle}
              className={cn('grid h-14 items-center border-b border-line-faint px-3.5', rowWidth)}>
              <button type="button"
                onClick={() => navigate(`/settings/people?person=${person.id}&tab=profile`)}
                className="flex min-w-0 items-center gap-2.5 text-left">
                <Avatar name={person.name} size={30} />
                <span className="flex min-w-0 flex-col">
                  <b className="truncate text-[13px] font-bold text-text hover:underline">{person.name}</b>
                  <span className="text-[11.5px] text-text-muted">
                    {plural(person.openRequests, 'open request', 'open requests')}
                  </span>
                </span>
              </button>
              {grid.divisions.map((division) => (
                <div key={division.id} className="flex items-center gap-1">
                  {BOTH.map((segment) => {
                    const tick = { divisionId: division.id, segment }
                    const on = has(person, tick)
                    return (
                      <Letter
                        key={segment}
                        letter={SEGMENT_LETTER[segment]}
                        look={on ? 'on' : 'off'}
                        label={`${person.name} · ${division.name} · ${SEGMENT_WORD[segment]}`}
                        onClick={canEdit ? () => void toggle(person.id, tick, !on) : undefined}
                      />
                    )
                  })}
                </div>
              ))}
              <span className="flex min-w-0 flex-col leading-[1.3]">
                <span className="text-[12.5px] text-text">{person.startsFrom}</span>
                <span className="text-[11.5px] text-text-muted">{person.works}</span>
              </span>
            </div>
          ))}

          {/* *Not covered*: a dashed amber letter under every pair nobody has. */}
          <div style={rowStyle}
            className={cn('grid h-11 items-center bg-amber-soft px-3.5 text-[12px] text-amber-band-text', rowWidth)}>
            <b>Not covered</b>
            {grid.divisions.map((division) => {
              const gaps = gapsIn(division.id)
              return (
                <div key={division.id} className="flex items-center gap-1">
                  {gaps.length > 0 && BOTH.map((segment) => (
                    <Letter key={segment} letter={SEGMENT_LETTER[segment]}
                      look={gaps.some((one) => one.segment === segment) ? 'gap' : 'none'} />
                  ))}
                </div>
              )
            })}
            <span>{plural(grid.notCovered.length, 'gap', 'gaps')}</span>
          </div>
        </div>
      </div>

      <p className="m-0 mt-2.5 text-[12px] leading-[1.5] text-text-muted">
        Click R or C to switch it. A name opens the person’s <b>Sells</b> card — the same data.
        Residential is the Residential type; commercial is every other type.
      </p>

      <div className={cn('mt-4 flex items-start gap-4', phone && 'flex-col items-stretch')}>
        <Card className={phone ? 'w-full' : 'w-[520px] shrink-0'}>
          <b className="text-[14.5px] font-bold text-text">How a salesperson is recommended</b>
          <Step n={1}>
            They cover the property’s <b>branch</b>, the job’s <b>division</b>, and <b>residential</b> or{' '}
            <b>commercial</b> — a tick here.
          </Step>
          <Step n={2}>Ordered by the grade of their best time in the next two weeks, near the property.</Step>
          <Step n={3}>
            Then by fewer open requests. Someone who sold to this customer before goes first among equals.
          </Step>
          <Step n={4}>
            Nobody covers it? Everyone with the Sales role in the branch is shown, with a line that says so.
          </Step>
        </Card>

        <Card className="min-w-0 flex-1">
          <b className="text-[14.5px] font-bold text-text">Gaps in {branchName}</b>
          {/* 6px between bands of a kind, 14 where the slate ones start (Board 11). */}
          <div className="mt-2.5 flex flex-col gap-1.5">
            {grid.notCovered.length === 0 && grid.single.length === 0 && (
              <p className="m-0 text-[12.5px] text-text-muted">Every division is covered by two or more people.</p>
            )}
            {grid.notCovered.map((gap) => (
              <Note key={`${gap.divisionId}-${gap.segment}`} tone="amber"
                title={`${divisionName(gap.divisionId)} · ${SEGMENT_WORD[gap.segment]}`}>
                — nobody. Requests fall back to everyone in {branchName}.
              </Note>
            ))}
            {grid.single.map((pair, index) => (
              <Note key={`${pair.divisionId}-${pair.segment}`} tone="slate"
                className={index === 0 && grid.notCovered.length > 0 ? 'mt-2' : undefined}
                title={`${divisionName(pair.divisionId)} · ${SEGMENT_WORD[pair.segment]}`}>
                — only {pair.name}.
              </Note>
            ))}
          </div>
        </Card>
      </div>

      {visits && (
        <Card className="mt-4">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-[9px] border border-slate-band-line bg-slate-band-bg text-slate-band-text">
              <OfficeGlyph />
            </span>
            <div className="flex flex-col">
              <b className="text-[14.5px] font-bold text-text">Site visits — the whole company</b>
              <span className="text-[12px] text-text-muted">the same in every branch</span>
            </div>
          </div>
          <div className={cn('mt-3.5 grid items-center gap-x-[18px] gap-y-3.5',
            phone ? 'grid-cols-1' : 'grid-cols-[200px_1fr]')}>
            <span className="text-[13px] font-semibold text-text">Arrival window</span>
            <div className={cn('flex gap-3', phone ? 'flex-col items-start' : 'items-center')}>
              <Segmented<string>
                label="Arrival window"
                size="large"
                value={String(visits.arrivalMinutes)}
                options={ARRIVAL_WINDOWS.map((one) => ({
                  value: String(one.minutes), label: one.label, disabled: !canEdit,
                }))}
                onChange={(value) => void saveVisits({ arrivalMinutes: Number(value) })}
              />
              <span className="text-[12px] text-text-muted">
                customers are told “{arrivalWords(EXAMPLE_START, visits.arrivalMinutes)}”
              </span>
            </div>
            <span className="text-[13px] font-semibold text-text">Confirmation</span>
            <div className="flex items-center gap-2.5">
              <Switch
                label="Confirmation"
                hideLabel
                checked={visits.confirmDefault}
                disabled={!canEdit}
                onChange={(checked) => void saveVisits({ confirmDefault: checked })}
              />
              <span className="text-[12.5px] text-text-body">
                starts on in every New request — by text or email, as the contact prefers
              </span>
            </div>
            <span className="text-[13px] font-semibold text-text">Same-day visits</span>
            <span className="text-[12.5px] text-text-body">offered from 30 min after now, on the quarter hour</span>
          </div>
        </Card>
      )}

      {grid.branch && (
        <AddPerson
          orgUnitId={grid.branch.id}
          anchor={addButton.current}
          open={adding}
          onClose={() => setAdding(false)}
          onAdded={setGrid}
        />
      )}
    </div>
  )
}

function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <section className={cn('rounded-record border border-line bg-surface p-[18px]', className)}>
      {children}
    </section>
  )
}

/** One of the four steps: a dark green number and its sentence (Board 11). */
function Step({ n, children }: { n: number; children: ReactNode }) {
  return (
    <div className="mt-2.5 flex gap-2.5 text-[12.5px] leading-[1.45] text-text-body">
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent-dark text-[11px] font-bold text-surface">
        {n}
      </span>
      <span>{children}</span>
    </div>
  )
}

/** A gap: amber when nobody covers it, slate when one person does. */
function Note({ tone, title, className, children }: {
  tone: 'amber' | 'slate'; title: string; className?: string; children: ReactNode
}) {
  return (
    <div className={cn('flex items-start gap-[9px] rounded-[10px] border px-3 py-2.5 text-[12px] leading-[1.45]',
      className,
      tone === 'amber' ? 'border-amber-band-line bg-amber-band-bg text-amber-band-text'
        : 'border-slate-band-line bg-slate-band-bg text-slate-band-text')}>
      <span className="mt-px inline-flex shrink-0">
        {tone === 'amber' ? <IconWarn size={14} /> : <IconInfo size={14} />}
      </span>
      <span><b className="font-bold">{title}</b> {children}</span>
    </div>
  )
}

/** Board 11's office: one block with six windows on a ground line — not the tower-and-wings icon. */
function OfficeGlyph() {
  return (
    <svg viewBox="0 0 24 24" width={15} height={15} aria-hidden fill="none" stroke="currentColor"
      strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 21h18M5 21V4h14v17M9 8h2M13 8h2M9 12h2M13 12h2M9 16h2M13 16h2" />
    </svg>
  )
}
